import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  Box,
  Grid,
  GridItem,
  HStack,
  Heading,
  VStack,
  useTheme,
} from '@frontend/components';

import { PageTitle } from 'components/PageTitle';

import { getAvailableGroups, getUser } from 'store/session/selectors';

import { useSearchParams } from 'utils/hooks';
import { UserGroupCollectionProvider } from 'utils/multisite';

import { UserGroupFilter } from './components/UserGroupFilter';
import { AssignedToYouPane } from './panes/AssignedToYouPane';
import { InternalQuotesPane } from './panes/InternalQuotesPane';
import { OrdersPane } from './panes/OrdersPane';
import { QuotesOverviewPane } from './panes/QuotesOverviewPane';
import { TeamAssignmentsPane } from './panes/TeamAssignments';

const getTimeOfDay = () => {
  const now = new Date();
  const hours = now.getHours();
  return hours < 12 ? 'morning' : hours < 18 ? 'afternoon' : 'evening';
};

const useTimeOfDay = () => {
  const [timeOfDay, setTimeOfDay] = useState(getTimeOfDay());
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeOfDay(getTimeOfDay());
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return timeOfDay;
};

export const MultiSiteDashboard = () => {
  const theme = useTheme();
  const timeOfDay = useTimeOfDay();
  const user = useSelector(getUser);
  const availableGroups = useSelector(getAvailableGroups);

  const [{ groups }, setSearchParams] = useSearchParams<{ groups: number[] }>();

  const activeGroupIds = groups?.split(',')?.map(Number) ?? availableGroups;
  const setActiveGroupIds = useCallback(
    (nextActiveGroupIds: number[]) => {
      const allGroupsSelected = availableGroups.every((id) =>
        nextActiveGroupIds.includes(id),
      );
      setSearchParams({
        groups: allGroupsSelected ? undefined : nextActiveGroupIds,
      });
    },
    [setSearchParams, availableGroups],
  );

  return (
    <UserGroupCollectionProvider>
      <VStack align="stretch" padding={3} gap={2} minHeight="100%">
        <PageTitle title="🥷 Multi-Site Dashboard" />
        <HStack as={Heading} size="xl" gap={1.5}>
          <Box as="span">
            Good {timeOfDay}, {user?.firstName}
          </Box>
          <UserGroupFilter
            availableGroupIds={availableGroups}
            activeGroupIds={activeGroupIds}
            onApply={setActiveGroupIds}
          />
        </HStack>
        <Grid
          templateAreas={{
            base: `
            "quotes"
            "internal-quotes"
            "assigned-to-you"
            "orders"
            "team-assignments"
          `,
            tablet: `
            "quotes quotes"
            "internal-quotes assigned-to-you"
            "orders team-assignments"
          `,
            desktop: `
            "quotes quotes assigned-to-you"
            "quotes quotes assigned-to-you"
            "orders internal-quotes assigned-to-you"
            "team-assignments internal-quotes assigned-to-you"
          `,
            desktopLarge: `
            "quotes quotes internal-quotes assigned-to-you"
            "quotes quotes internal-quotes assigned-to-you"
            "orders team-assignments internal-quotes assigned-to-you"
          `,
          }}
          templateColumns={{
            base: 'minmax(0, 1fr)',
            tablet: '1fr 1fr',
            desktop: `1fr 1fr 1fr`,
            desktopLarge: `1fr 1fr 1fr 1fr`,
            desktopXLarge: `1fr 1fr minmax(auto, ${theme.space[60]}) minmax(auto, ${theme.space[60]})`,
          }}
          gap={2}
        >
          <GridItem gridArea="quotes">
            <QuotesOverviewPane activeGroupIds={activeGroupIds} />
          </GridItem>
          <GridItem gridArea="internal-quotes">
            <InternalQuotesPane activeGroupIds={activeGroupIds} />
          </GridItem>
          <GridItem gridArea="assigned-to-you">
            <AssignedToYouPane activeGroupIds={activeGroupIds} />
          </GridItem>
          <GridItem gridArea="orders">
            <OrdersPane activeGroupIds={activeGroupIds} />
          </GridItem>
          <GridItem gridArea="team-assignments">
            <TeamAssignmentsPane activeGroupIds={activeGroupIds} />
          </GridItem>
        </Grid>
      </VStack>
    </UserGroupCollectionProvider>
  );
};
